import { UserMenu } from "./UserMenu";
import { NotificationsMenu } from "./NotificationsMenu";
import { PointsDisplay } from "@/components/referral/PointsDisplay";
import { Logo } from "@/components/ui/logo";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/contexts/ThemeContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Menu, Home, Users, Heart, Lightbulb, Bot, Settings, LogOut, LogIn, Info, Mail, HelpCircle, Shield, FileText, BookOpen, User, Gift, Clock, Calendar, Activity, Chrome, MessageCircleQuestion, Skull, Gamepad2, ChevronDown, Brain, UserPlus, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate, useLocation } from "react-router";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";
import React, { useState, useEffect } from "react";
import { isNative } from "@/utils/platform";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const native = isNative();

  const [menuOpen, setMenuOpen] = useState(false);

  const handleNavigation = (href: string) => {
    setMenuOpen(false);
    if (location.pathname !== href) {
      navigate(href);
    }
    // Do nothing if already on the same page to prevent reload
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
  };

  const isActive = (href: string) => location.pathname === href;
  const isGamesActive = () => ['/randomizer', '/friend-quiz', '/mafia', '/sync'].some(route => location.pathname.startsWith(route));

  const [gamesOpen, setGamesOpen] = useState(() => {
    const gamesRoutes = ['/randomizer', '/friend-quiz', '/mafia', '/sync'];
    return gamesRoutes.some(route => location.pathname.startsWith(route));
  });

  useEffect(() => {
    const gamesRoutes = ['/randomizer', '/friend-quiz', '/mafia', '/sync'];
    if (gamesRoutes.some(route => location.pathname.startsWith(route))) {
      setGamesOpen(true);
    }
  }, [location.pathname]);

  const menuItems = [
    { icon: User, label: "Profile", href: "/" },
    { icon: Heart, label: "Wishlist", href: "/wishlist" },
    { icon: Users, label: "Friends", href: "/friends" },
    { icon: UserPlus, label: "Referrals", href: "/referrals" },
    { icon: Clock, label: "Reminders", href: "/reminders" },
    { icon: Calendar, label: "Events", href: "/events" },
    { icon: Bot, label: "AI Assistant", href: "/ai-assistant" },
    { icon: Settings, label: "Settings", href: "/settings" },
    { icon: LogOut, label: "Logout", href: "#", onClick: handleLogout, isLogout: true }
  ];

  const gamesItems = [
    { icon: Gift, label: "Randomizer", href: "/randomizer" },
    { icon: MessageCircleQuestion, label: "Friend Quiz", href: "/friend-quiz" },
    { icon: Skull, label: "Mafia Game", href: "/mafia" },
    { icon: Brain, label: "Sync Game", href: "/sync" },
  ];

  const commonMenuItems = [
    { icon: Info, label: "About", href: "/about" },
    { icon: Activity, label: "Status", href: "https://status.buyfors.com", external: true },
    { icon: Mail, label: "Contact", href: "/contact" },
    { icon: HelpCircle, label: "FAQ", href: "/faq" },
    { icon: Shield, label: "Privacy Policy", href: "/privacy-policy" },
    { icon: FileText, label: "Terms of Service", href: "/terms-of-service" },
    { icon: BookOpen, label: "Impressum", href: "/impressum" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 h-[calc(4rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 z-50 animate-fade-in">
      <div className="container h-16 mx-auto px-4">
        <div className="h-full flex items-center justify-between">
          <div className="flex items-center">
            <Logo />
          </div>
          {user && (
            <div className="flex items-center gap-4">
              <PointsDisplay />
              <NotificationsMenu />
              <div className="hidden lg:block">
                <UserMenu />
              </div>
              <div className={cn("lg:hidden", native && "hidden")}>
                <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                  <SheetTrigger asChild>
                    <button className="p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none active:bg-gray-200 dark:active:bg-gray-600">
                      <Menu className="h-6 w-6" color="#9b87f5" />
                    </button>
                  </SheetTrigger>
                    <SheetContent side="right" className="w-64 p-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] backdrop-blur-xl bg-gradient-to-b from-white/95 via-white/90 to-[#F1F0FB]/95 dark:from-gray-900/95 dark:via-gray-800/90 dark:to-gray-900/95 border-l border-gray-200/60 dark:border-gray-700/60 shadow-xl">
                      <VisuallyHidden>
                        <SheetTitle>Navigation Menu</SheetTitle>
                        <SheetDescription>
                          Application navigation options and user menu
                        </SheetDescription>
                      </VisuallyHidden>
                      <div className="flex flex-col h-full">
                        <ScrollArea className="flex-1 py-6">
                          <div className="flex flex-col">
                            <ul className="space-y-1 px-3" aria-label="Main navigation">
                              {menuItems.slice(0, 5).map((item) => {
                                const active = isActive(item.href);
                                const isLogout = item.isLogout;
                                
                                return (
                                  <li key={item.label}>
                                    <button
                                      onClick={() => item.onClick ? item.onClick() : handleNavigation(item.href)}
                                      className={cn(
                                        "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden",
                                        isLogout ? "text-red-600 dark:text-red-400" : "text-gray-700 dark:text-gray-200",
                                        active && !isLogout && "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold border-l-4 border-primary",
                                        !active && !isLogout && "hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-primary hover:scale-[1.02]"
                                      )}
                                    >
                                      <div className={cn(
                                        "p-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-sm group-hover:shadow-md",
                                        isLogout 
                                          ? "bg-red-50 dark:bg-red-900/30 group-hover:bg-red-100 dark:group-hover:bg-red-900/50"
                                          : active 
                                            ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20" 
                                            : "bg-white/80 dark:bg-gray-700/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
                                      )}>
                                        <item.icon className={cn(
                                          "h-5 w-5 transition-all duration-300",
                                          active && !isLogout && "scale-110",
                                          !active && !isLogout && "group-hover:scale-110 group-hover:rotate-3"
                                        )} />
                                      </div>
                                      <span className={cn(
                                        "font-medium text-base transition-all duration-300",
                                        active && !isLogout && "translate-x-1"
                                      )}>
                                        {item.label}
                                      </span>
                                    </button>
                                  </li>
                                );
                              })}

                              {/* Games Collapsible Menu */}
                              <li>
                                <Collapsible open={gamesOpen} onOpenChange={setGamesOpen}>
                                  <CollapsibleTrigger asChild>
                                    <button
                                      className={cn(
                                        "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden text-gray-700 dark:text-gray-200",
                                        isGamesActive() && "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold",
                                        !isGamesActive() && "hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-primary hover:scale-[1.02]"
                                      )}
                                    >
                                      <div className={cn(
                                        "p-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-sm group-hover:shadow-md",
                                        isGamesActive() 
                                          ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20" 
                                          : "bg-white/80 dark:bg-gray-700/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
                                      )}>
                                        <Gamepad2 className={cn(
                                          "h-5 w-5 transition-all duration-300",
                                          isGamesActive() && "scale-110",
                                          !isGamesActive() && "group-hover:scale-110 group-hover:rotate-3"
                                        )} />
                                      </div>
                                      <span className={cn(
                                        "font-medium text-base transition-all duration-300 flex-1 text-left",
                                        isGamesActive() && "translate-x-1"
                                      )}>
                                        Games
                                      </span>
                                      <ChevronDown className={cn(
                                        "h-4 w-4 transition-transform duration-200",
                                        gamesOpen && "rotate-180"
                                      )} />
                                    </button>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent className="space-y-1 mt-1 pl-4">
                                    {gamesItems.map((gameItem) => {
                                      const gameActive = isActive(gameItem.href);
                                      return (
                                        <button
                                          key={gameItem.label}
                                          onClick={() => handleNavigation(gameItem.href)}
                                          className={cn(
                                            "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden text-gray-700 dark:text-gray-200",
                                            gameActive && "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold border-l-4 border-primary",
                                            !gameActive && "hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-primary hover:scale-[1.02]"
                                          )}
                                        >
                                          <div className={cn(
                                            "p-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-sm group-hover:shadow-md",
                                            gameActive 
                                              ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20" 
                                              : "bg-white/80 dark:bg-gray-700/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
                                          )}>
                                            <gameItem.icon className={cn(
                                              "h-5 w-5 transition-all duration-300",
                                              gameActive && "scale-110",
                                              !gameActive && "group-hover:scale-110 group-hover:rotate-3"
                                            )} />
                                          </div>
                                          <span className={cn(
                                            "font-medium text-base transition-all duration-300",
                                            gameActive && "translate-x-1"
                                          )}>
                                            {gameItem.label}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </CollapsibleContent>
                                </Collapsible>
                              </li>

                              {menuItems.slice(5).map((item) => {
                                const active = isActive(item.href);
                                const isLogout = item.isLogout;
                                
                                return (
                                  <React.Fragment key={item.label}>
                                    <li>
                                      <button
                                        onClick={() => item.onClick ? item.onClick() : handleNavigation(item.href)}
                                        className={cn(
                                          "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden",
                                          isLogout ? "text-red-600 dark:text-red-400" : "text-gray-700 dark:text-gray-200",
                                          active && !isLogout && "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold border-l-4 border-primary",
                                          !active && !isLogout && "hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-primary hover:scale-[1.02]"
                                        )}
                                      >
                                        <div className={cn(
                                          "p-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-sm group-hover:shadow-md",
                                          isLogout 
                                            ? "bg-red-50 dark:bg-red-900/30 group-hover:bg-red-100 dark:group-hover:bg-red-900/50"
                                            : active 
                                              ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20" 
                                              : "bg-white/80 dark:bg-gray-700/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
                                        )}>
                                          <item.icon className={cn(
                                            "h-5 w-5 transition-all duration-300",
                                            active && !isLogout && "scale-110",
                                            !active && !isLogout && "group-hover:scale-110 group-hover:rotate-3"
                                          )} />
                                        </div>
                                        <span className={cn(
                                          "font-medium text-base transition-all duration-300",
                                          active && !isLogout && "translate-x-1"
                                        )}>
                                          {item.label}
                                        </span>
                                      </button>
                                    </li>
                                    {item.label === "Settings" && (
                                      <li>
                                        <button
                                          onClick={() => toggleTheme()}
                                          className={cn(
                                            "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative overflow-hidden text-gray-700 dark:text-gray-200",
                                            "hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-primary hover:scale-[1.02]"
                                          )}
                                        >
                                          <div className="p-2 rounded-lg transition-all duration-300 flex items-center justify-center shadow-sm group-hover:shadow-md bg-white/80 dark:bg-gray-700/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20">
                                            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                                          </div>
                                          <span className="font-medium text-base transition-all duration-300">Change theme</span>
                                        </button>
                                      </li>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </ul>

                            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                              <h3 className="px-6 mb-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-300 dark:to-gray-600" />
                                <span>Pages</span>
                                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-300 dark:to-gray-600" />
                              </h3>
                              <ul className="space-y-1 px-3" aria-label="Page navigation">
                                 {commonMenuItems.map((item) => {
                                  const active = isActive(item.href);
                                  
                                  return (
                                    <li key={item.label}>
                                      {item.external ? (
                                        <a
                                          href={item.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={() => setMenuOpen(false)}
                                          className={cn(
                                            "w-full flex items-center gap-3 px-4 py-2 text-gray-600 dark:text-gray-300 rounded-lg transition-all duration-300 group relative overflow-hidden",
                                            "hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-primary hover:scale-[1.02]"
                                          )}
                                        >
                                          <div className={cn(
                                            "p-2 rounded-lg transition-all duration-300 flex items-center justify-center",
                                            "bg-white/80 dark:bg-gray-700/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
                                          )}>
                                            <item.icon className={cn(
                                              "h-4 w-4 transition-all duration-300",
                                              "group-hover:scale-110 group-hover:rotate-3"
                                            )} />
                                          </div>
                                          <span className={cn(
                                            "text-sm font-medium transition-all duration-300"
                                          )}>
                                            {item.label}
                                          </span>
                                        </a>
                                      ) : (
                                        <button
                                          onClick={() => handleNavigation(item.href)}
                                          className={cn(
                                            "w-full flex items-center gap-3 px-4 py-2 text-gray-600 dark:text-gray-300 rounded-lg transition-all duration-300 group relative overflow-hidden",
                                            active && "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-semibold border-l-4 border-primary",
                                            !active && "hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-primary hover:scale-[1.02]"
                                          )}
                                        >
                                          <div className={cn(
                                            "p-2 rounded-lg transition-all duration-300 flex items-center justify-center",
                                            active 
                                              ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20" 
                                              : "bg-white/80 dark:bg-gray-700/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
                                          )}>
                                            <item.icon className={cn(
                                              "h-4 w-4 transition-all duration-300",
                                              active && "scale-110",
                                              !active && "group-hover:scale-110 group-hover:rotate-3"
                                            )} />
                                          </div>
                                          <span className={cn(
                                            "text-sm font-medium transition-all duration-300",
                                            active && "translate-x-1"
                                          )}>
                                            {item.label}
                                          </span>
                                        </button>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>

                            {user && (
                              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                                <ul className="space-y-1 px-3" aria-label="Extension">
                                  <li>
                                    <a
                                      href="https://chromewebstore.google.com/detail/fncfihbdgihnnjjjbjnejafcaliidleg"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() => setMenuOpen(false)}
                                      className={cn(
                                        "w-full flex items-center gap-3 px-4 py-2 text-gray-600 dark:text-gray-300 rounded-lg transition-all duration-300 group relative overflow-hidden",
                                        "hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-primary hover:scale-[1.02]"
                                      )}
                                    >
                                      <div className={cn(
                                        "p-2 rounded-lg transition-all duration-300 flex items-center justify-center",
                                        "bg-white/80 dark:bg-gray-700/80 group-hover:bg-gradient-to-br group-hover:from-primary/10 group-hover:to-primary/20"
                                      )}>
                                        <Chrome className={cn(
                                          "h-4 w-4 transition-all duration-300",
                                          "group-hover:scale-110 group-hover:rotate-3"
                                        )} />
                                      </div>
                                      <span className="text-sm font-medium transition-all duration-300">
                                        Install Extension
                                      </span>
                                    </a>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                        
                        {/* Decorative gradient overlay at bottom */}
                        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#F1F0FB]/50 to-transparent pointer-events-none" />
                      </div>
                    </SheetContent>
                </Sheet>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
